/* eslint-disable no-restricted-globals */
import { useEffect, useRef, useState } from 'react';
import { useMenu, useAppDispatch, useAppSelector } from 'src/hooks';
import {
  Box,
  Grid,
  Button,
  styled,
  Menu,
  MenuItem,
  Dialog,
  TextField,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Tooltip,
  AlertColor
} from '@mui/material';
import { AlertDialog, DialogResult } from 'src/components/analytics';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import _ from 'lodash';
import MODULE_TO_PAGE_MAP from '../../../home/types/simulation-module-to-page-map';
import {
  resetInputFieldValues,
  closeSkuPop,
  setskuPopupType,
  resetDuplicateDialog
} from '../../store/innovation-slice';
import {
  createNewSku,
  editSku,
  getSaveSimulatedInnovation
} from '../../store/innovation-slice-async-thunks';
import DuplicateSku from '../duplicate-sku-component/duplicate-sku';
import { SKUPopupTitle, SKUPopupType } from '../../enums/sku-popup-enum';
import { getFormattedSkuObject } from '../../utils/utils';
import LegendBox from '../../../components/legend-box/legend-box';
import { formatNumber } from '../../../pricing-simulator';
import SaveAsContainer from '../../../components/save-as-container/save-as-container';
import { saveScenarioDetails } from '../../../pricing-simulator/store/pricing-simulator-slice';
import SaveScenarioDialog from '../../../pricing-simulator/components/save-scenario-dialog/save-scenario-dialog';
import { ModuleSaveButton } from '../../../common/components';
import SnackbarComponent from '../snackbar/snackbar';
import AddSKUDialog from '../create-sku-component/innovation-add-sku';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(0.6, 1),
  fontSize: '12px'
}));

export interface ScenarioInfo {
  name: string;
  description: string;
  duplicateName: boolean;
}

export default function InnovationStrip({
  currSourceId,
  currModuleId,
  currScenarioID
}: {
  currSourceId: number;
  currModuleId: number;
  currScenarioID: number;
}) {
  // app dispatch for dispatching actions
  const appDispatch = useAppDispatch();
  // Getting state of innovation
  const {
    skuDetailsObj,
    selectedSkuDetails,
    skuPopupType,
    innovationTableMetaData,
    prevSkuName,
    appliedFilters,
    skusInnovatedCount,
    isSkuPopupVisible,
    isSimulated
  } = useAppSelector((state) => state.innovation);
  const summaryStripData = innovationTableMetaData.summary_strip;

  const { innovation: innovationAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  //  local state for create sku dialog
  const [isDialogOpen, setDialog] = useState(false);

  // local state for duplicate sku dialog
  const [isDuplicateDialogOpen, setDuplicateDialog] = useState(false);
  const [isManualOverwriteDialogOpen, setManualOverwriteDialog] =
    useState(false);
  // local state for save scenario
  const [isOpenSaveScenarioModal, setIsOpenSaveScenarioModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [scenarioData, setScenarioData] = useState({});
  const [openOverrideDialog, setOverrideDialogValue] = useState(false);

  // local state for snackbar open/close
  const [isSnackbarOpen, setSnackbar] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: '',
    snackbarType: 'success'
  });

  // getting user, scenario and source details from useModuleMetadata hooks
  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.INNOVATION
  });

  // local state for menu item
  const { isMenuOpen, anchorEl, handleMenuOpen, handleMenuClose } = useMenu();

  const [isSubmitDisabled, setSubmitDisable] = useState(true);

  //  handler for checking all input fields are non-empty
  const areFieldsEmpty = () => {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const key in selectedSkuDetails) {
      const objValue = selectedSkuDetails[key];
      if (Array.isArray(objValue) && objValue.length === 0) {
        setSubmitDisable(true);
        return true; // Found an empty array
      }
      if (typeof objValue === 'string' && objValue.trim() === '') {
        setSubmitDisable(true);
        return true; // Found an empty string
      }
      // }
    }
    setSubmitDisable(false);
    return false; // No empty values found
  };

  useEffect(() => {
    areFieldsEmpty();
  }, [selectedSkuDetails]);

  // handler for opening Create Sku dialog via menu option
  const handleClickOpen = (event) => {
    if (SKUPopupType.CREATE) {
      appDispatch(setskuPopupType(SKUPopupType.CREATE));
      // setting create dialog to true
      setDialog(true);
    } else if (SKUPopupType.OVERWRITE) {
      appDispatch(setskuPopupType(SKUPopupType.OVERWRITE));
      // setting create dialog to true
      setManualOverwriteDialog(true);
    }
    // closing the menu item
    handleMenuClose();
  };

  // handler for closing Create/Edit Sku dialog via menu option
  const handleClose = (
    event,
    reason,
    isSnackbarToOpen,
    snackbarText,
    snackbarType: AlertColor
  ) => {
    if (reason && reason === 'backdropClick') return;
    setDialog(false);
    setManualOverwriteDialog(false);
    appDispatch(closeSkuPop(false));
    if (isSnackbarToOpen) {
      setSnackbarContent({ snackbarText, snackbarType });
      setSnackbar(true);
    }
  };

  // handler to open Duplicate Sku dialog via menu option
  const handleOpenDuplicate = () => {
    // resetting filters and table on duplicate dialog
    appDispatch(resetDuplicateDialog());
    setDuplicateDialog(true);
    handleMenuClose();
  };

  // handler to close Duplicate Sku dialog
  const handleCloseDuplicate = (
    isSnackbarToOpen,
    snackbarText,
    snackbarType
  ) => {
    setDuplicateDialog(false);
    if (isSnackbarToOpen) {
      setSnackbarContent({ snackbarText, snackbarType });
      setSnackbar(true);
    }
  };

  // handler to open Manual Overwrite Sku dialog via menu option
  const handleOpenManualOverwrite = () => {
    // resetting filters and table on duplicate dialog
    appDispatch(resetDuplicateDialog());
    setManualOverwriteDialog(true);
    handleMenuClose();
  };

  // handler to close Duplicate Sku dialog
  const handleCloseManualOverwrite = (
    isSnackbarToOpen,
    snackbarText,
    snackbarType
  ) => {
    setManualOverwriteDialog(false);
    if (isSnackbarToOpen) {
      setSnackbarContent({ snackbarText, snackbarType });
      setSnackbar(true);
    }
  };

  // handler for save simulation
  const saveSimulation = () =>
    appDispatch(
      getSaveSimulatedInnovation({
        sourceID: sourceId,
        moduleID: moduleId,
        scenarioID: scenarioId
      })
    ).then((res) => {
      if (res.meta.requestStatus === 'rejected') {
        setSnackbarContent({
          // eslint-disable-next-line dot-notation
          snackbarText: res['error'].message,
          snackbarType: 'error'
        });
        setSnackbar(true);
      }
    });

  // handler for save dialog
  const handleSaveClose = (value: ScenarioInfo) => {
    setOpen(false);
    setIsOpenSaveScenarioModal(false);
    if (value.duplicateName === true) {
      setScenarioData(value);
      setOverrideDialogValue(true);
    } else {
      appDispatch(saveScenarioDetails(value));
    }
  };

  return (
    <>
      {/* grid for innovation info box and legend */}
      <Grid
        container
        spacing={1}
        justifyContent='space-between'
        alignItems='center'
      >
        <Grid item xs>
          <Button
            onClick={handleMenuOpen}
            type='button'
            variant='contained'
            disabled={!innovationAccessConfig.add_sku}
          >
            <ControlPointIcon sx={{ marginRight: '4px', fontSize: '18px' }} />
            Add SKU
          </Button>
          <div
            className='info-box-div'
            style={{ display: 'inline-block', marginLeft: '10px' }}
          >
            <div className='info-box info-box-label sku-delist-label-container'>
              <span className='info-box'>
                <span className=' info-box-label'>
                  {' '}
                  <span
                  // eslint-disable-next-line react/no-unescaped-entities
                  >
                    SKU’s Innovated:
                  </span>
                </span>
                <span className='info-value'>{skusInnovatedCount}</span>
              </span>
            </div>
          </div>
        </Grid>
        {/* eslint-disable arrow-body-style  */}

        <Grid item xs={4}>
          <Grid
            container
            spacing={1}
            justifyContent='end'
            alignItems='center'
            mt={0}
          >
            {innovationTableMetaData.legend.metadata &&
              innovationTableMetaData.legend.metadata.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  {innovationTableMetaData.legend.metadata.map((legendData) => (
                    <div style={{ marginRight: '6px' }}>
                      <LegendBox legendMetadata={legendData} />
                    </div>
                  ))}
                </div>
              )}
            <div style={{ paddingTop: '0px', marginLeft: '6px' }}>
              <SaveAsContainer disabled={!innovationAccessConfig?.save_as} />
            </div>

            <div>
              <ModuleSaveButton
                onSave={saveSimulation}
                disabled={!innovationAccessConfig?.save}
              />
              {isOpenSaveScenarioModal && (
                <SaveScenarioDialog open={open} onClose={handleSaveClose} />
              )}
            </div>
          </Grid>
        </Grid>
      </Grid>
      {/* create/edit sku dialog */}
      {isDialogOpen || isSkuPopupVisible ? (
        <AddSKUDialog
          isCreateDialogOpen={isDialogOpen || isSkuPopupVisible}
          setCreateDialog={(isSnackbarToOpen, snackbarText, snackbarType) =>
            handleClose(
              event,
              'reason',
              isSnackbarToOpen,
              snackbarText,
              snackbarType
            )
          }
        />
      ) : null}
      {/* bulk duplicate sku dialog */}
      {isDuplicateDialogOpen ? (
        <DuplicateSku
          isDupDialogOpen={isDuplicateDialogOpen}
          setDupDialog={(isSnackbarToOpen, snackbarText, snackbarType) =>
            handleCloseDuplicate(isSnackbarToOpen, snackbarText, snackbarType)
          }
        />
      ) : null}

      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
        sx={{ padding: '4px 0 4px 0' }}
      >
        <StyledMenuItem onClick={handleClickOpen}>
          <ControlPointIcon
            fontSize='inherit'
            color='primary'
            sx={{ marginRight: '4px' }}
          />
          Create new SKU
        </StyledMenuItem>
        <StyledMenuItem
          onClick={handleOpenDuplicate}
          disabled={!innovationAccessConfig.duplicate_bulk}
        >
          <ContentCopyIcon
            fontSize='inherit'
            color='primary'
            sx={{ marginRight: '4px' }}
          />
          Duplicate from an existing SKU
        </StyledMenuItem>
      </Menu>
      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setSnackbar}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />
    </>
  );
}
